import { useEffect, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const when = (stamp) => (stamp ? new Date(stamp).toLocaleString() : "never");

// Recapture the screens behind the 60-second tour, so it never shows an old design.
export const TourScreens = () => {
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);

  const look = async () => {
    const { data } = await api.get("/tour/refresh/last");
    setLast(data);
    return data;
  };

  useEffect(() => {
    look().then((data) => {
      if (data.state === "running") setBusy(true);  // a walk started elsewhere is still going
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // the walk takes a couple of minutes, so watch it from here
  useEffect(() => {
    if (!busy) return undefined;
    const beat = setInterval(async () => {
      try {
        const data = await look();
        if (data.state === "running") return;
        setBusy(false);
        if (data.state === "done") {
          toast.success(`Recaptured ${data.fresh.length} of ${data.fresh.length + (data.missed?.length || 0)} screens`);
          if (data.missed?.length) toast.warning(`${data.missed.length} screen(s) kept their old picture`);
        } else {
          toast.error(data.why || "No screen came back cleanly — the old tour is untouched");
        }
      } catch (e) {
        console.error("could not check on the tour walk", e);
      }
    }, 5000);
    return () => clearInterval(beat);
  }, [busy]);

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/tour/refresh");
      toast.info("MiLu is walking the app — this takes a couple of minutes");
    } catch (e) {
      setBusy(false);
      toast.error(apiError(e.response?.data?.detail) || "Could not recapture the tour");
    }
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="tour-screens">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
        The 60-second tour
      </p>
      <p className="mt-2 text-sm text-ink-soft">
        Press this after a redesign and MiLu walks the app with a headless browser, recapturing every
        screen behind the tour. A screen that fails keeps its old picture.
      </p>
      <button
        data-testid="tour-refresh"
        onClick={refresh}
        disabled={busy}
        className="mt-4 inline-flex min-h-[42px] items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
        {busy ? "Walking the app…" : "Refresh tour screens"}
      </button>
      <p className="mt-3 text-xs text-ink-soft" data-testid="tour-refresh-last">
        {busy || last?.state === "running"
          ? "Walking the app now…"
          : `Last refreshed: ${when(last?.at)}`}
        {!busy && last?.fresh?.length ? ` · ${last.fresh.join(", ")}` : ""}
        {!busy && last?.by ? ` · by ${last.by}` : ""}
      </p>
      {last?.missed?.length ? (
        <ul className="mt-2 space-y-1 text-xs text-amber-300" data-testid="tour-refresh-missed">
          {last.missed.map((m) => <li key={m.screen}>{m.screen}: {m.why}</li>)}
        </ul>
      ) : null}
    </div>
  );
};
